// Client for the CSM case-management API proxy routes under
// /api/protected/csm/*. Each route wraps the permission-service response as
// { response: <body> } (or { error } with a non-2xx status), so we unwrap here.

export interface CaseSummary {
  id: string;
  name: string;
  normalized_name: string;
  status: string;
  accessible_to_all: boolean;
  csam_flagged?: boolean;
  csam_flag_source?: 'auto' | 'manual' | null;
  csam_can_turn_off?: boolean;
}

export interface CaseAccessUser {
  grant_id: string;
  user_id: string;
  email: string;
  name: string;
  access_level: string;
  granted_at: string | null;
}

export interface TenantUser {
  id: string;
  email: string;
  name: string;
  status?: string;
}

export interface CaseSyncResult {
  tenant_id: string;
  created: number;
  updated: number;
  archived: number;
  active_total: number;
}

async function unwrap<T>(res: Response): Promise<T> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* empty body (e.g. 204) */
  }
  if (!res.ok) {
    const detail =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : res.statusText;
    throw new Error(`${res.status}: ${detail}`);
  }
  return (body && typeof body === 'object' && 'response' in body
    ? (body as { response: T }).response
    : (body as T));
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

function asArray<T>(data: unknown, label: string): T[] {
  // A successful HTTP response with a non-array body is an error, not "empty" —
  // surface it so the UI shows an error state rather than a misleading empty one.
  if (!Array.isArray(data)) {
    throw new Error(`${label}: expected an array response`);
  }
  return data as T[];
}

// The master switch is a security-relevant boolean: a missing/non-boolean field
// in an otherwise-successful response is a contract violation, not "off". Coercing
// it to false (Boolean(undefined)) would silently show the tenant as restricted
// when we simply don't know — surface it as an error so the UI shows the unknown
// state, exactly as asArray does for a non-array body.
function readAllowAccessFlag(data: unknown, label: string): boolean {
  const value =
    data && typeof data === 'object'
      ? (data as { allow_access_to_all_cases_in_tenant?: unknown })
          .allow_access_to_all_cases_in_tenant
      : undefined;
  if (typeof value !== 'boolean') {
    throw new Error(`${label}: missing boolean allow_access_to_all_cases_in_tenant`);
  }
  return value;
}

export async function listCases(signal?: AbortSignal): Promise<CaseSummary[]> {
  const res = await fetch('/api/protected/csm/cases', { signal });
  return asArray<CaseSummary>(await unwrap<unknown>(res), 'listCases');
}

export async function syncCases(): Promise<CaseSyncResult> {
  const res = await fetch('/api/protected/csm/cases/sync', { method: 'POST' });
  return unwrap<CaseSyncResult>(res);
}

export async function listCaseAccess(caseId: string, signal?: AbortSignal): Promise<CaseAccessUser[]> {
  const res = await fetch(`/api/protected/csm/cases/${caseId}/access`, { signal });
  return asArray<CaseAccessUser>(await unwrap<unknown>(res), 'listCaseAccess');
}

// The tenant-wide "everyone sees every case" master switch (READ-ONLY). When
// true, every user in the tenant sees every case and per-case grants are
// bypassed; when false, access is managed per case. The switch is owned by
// config-service and flipped in cell-manager (TR-1613) — permission-service
// exposes no write endpoint, so this is display-only here.
export async function getTenantCaseAccessSetting(signal?: AbortSignal): Promise<boolean> {
  const res = await fetch('/api/protected/csm/cases/access-setting', { signal });
  return readAllowAccessFlag(await unwrap<unknown>(res), 'getTenantCaseAccessSetting');
}

export async function grantCaseAccess(caseId: string, userId: string): Promise<CaseAccessUser> {
  const res = await fetch(`/api/protected/csm/cases/${caseId}/access`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ user_id: userId }),
  });
  return unwrap<CaseAccessUser>(res);
}

export async function revokeCaseAccess(caseId: string, userId: string): Promise<void> {
  const res = await fetch(`/api/protected/csm/cases/${caseId}/access/${userId}`, {
    method: 'DELETE',
  });
  await unwrap<unknown>(res);
}

export async function setCaseVisibility(
  caseId: string,
  accessibleToAll: boolean,
): Promise<CaseSummary> {
  const res = await fetch(`/api/protected/csm/cases/${caseId}/visibility`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ accessible_to_all: accessibleToAll }),
  });
  return unwrap<CaseSummary>(res);
}

// Uses the auto-scoping /api/protected/users proxy (not the /csm variant) so
// both CSM staff and tenant admins resolve correctly: CSM callers auto-route to
// the /csm path, tenant admins to the tenant path. Both return the same UserDTO
// shape from the shared users factory.
export async function listTenantUsers(signal?: AbortSignal): Promise<TenantUser[]> {
  const res = await fetch('/api/protected/users', { signal });
  return asArray<TenantUser>(await unwrap<unknown>(res), 'listTenantUsers');
}
